import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import PublicPageLayout from './PublicPageLayout';
import PublicToolPassportModal from './PublicToolPassportModal';
import { DataGrid } from '@mui/x-data-grid';
import { CircularProgress, Alert, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Select from 'react-select';

/**
 * PublicToolsList - Read-only tool list for public/user access
 * Shows all approved tools with filtering and search capabilities
 */
const PublicToolsList = () => {
    const { t } = useTranslation();

    // Data state
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter state
    const [panelOptions, setPanelOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination
    const [pageSize, setPageSize] = useState(10);

    // Logo hover state
    const [hoveredLogo, setHoveredLogo] = useState(null);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const handleMouseMove = (e) => {
        setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    const handleOpenModal = (row) => {
        setSelectedRow(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRow(null);
    };

    // Fetch tools data
    useEffect(() => {
        const fetchTools = async () => {
            try {
                setLoading(true);
                const res = await axios.get('/ToolPassports/Public');
                setTools(res.data || []);
                setError(null);
            } catch (err) {
                console.error('Error fetching tools:', err);
                setError(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        };
        fetchTools();
    }, [t]);

    // Fetch panel options
    useEffect(() => {
        const loadPanels = async () => {
            try {
                const res = await axios.get('/Panels/Public/SelectOptions');
                setPanelOptions(res.data || []);
            } catch (err) {
                console.error('Error loading panels:', err);
            }
        };
        loadPanels();
    }, []);

    // Handle panel change
    const handlePanelChange = async (option) => {
        setSelectedPanel(option);
        setSelectedCategory(null);
        setCategoryOptions([]);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);

        if (!option) return;
        try {
            const catRes = await axios.get(`/Categories/Public/SelectOptionsByPanel/${option.value}`);
            setCategoryOptions(Array.isArray(catRes.data) ? catRes.data : []);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    // Handle category change
    const handleCategoryChange = async (option) => {
        setSelectedCategory(option);
        setSelectedSubcategory(null);
        setSubcategoryOptions([]);
        if (!option) return;
        try {
            const subRes = await axios.get(`/Subcategories/Public/SelectOptionsByCategory/${option.value}`);
            setSubcategoryOptions(Array.isArray(subRes.data) ? subRes.data : []);
        } catch (error) {
            console.error('Error loading subcategories:', error);
        }
    };

    // Handle subcategory change
    const handleSubcategoryChange = (option) => {
        setSelectedSubcategory(option);
    };

    // Filter data
    const filteredData = useMemo(() => {
        let data = tools || [];

        if (selectedPanel) {
            data = data.filter(item => item.panelName === selectedPanel.label);
        }
        if (selectedCategory) {
            data = data.filter(item => item.categoryName === selectedCategory.label);
        }
        if (selectedSubcategory) {
            data = data.filter(item => item.subcategoryName === selectedSubcategory.label);
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                item.name?.toLowerCase().includes(query) ||
                item.typeOfToolName?.toLowerCase().includes(query) ||
                item.categoryName?.toLowerCase().includes(query) ||
                item.subcategoryName?.toLowerCase().includes(query)
            );
        }
        return data;
    }, [tools, selectedPanel, selectedCategory, selectedSubcategory, searchQuery]);

    // Define columns
    const columns = useMemo(() => [
        {
            field: 'logo',
            headerName: t('logo'),
            width: 100,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => {
                const logoPath = params.row.logo;
                const imageUrl = `${process.env.REACT_APP_SERVER_URLForLogo}/${logoPath}`;
                const defaultImageUrl = `/assets/brand/default_img.png`;

                return (
                    <div
                        onMouseEnter={() => setHoveredLogo(logoPath ? imageUrl : defaultImageUrl)}
                        onMouseLeave={() => setHoveredLogo(null)}
                        onMouseMove={handleMouseMove}
                        style={{ position: 'relative' }}
                    >
                        <img
                            src={logoPath ? imageUrl : defaultImageUrl}
                            alt="logo"
                            style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                            onError={(e) => { e.target.onerror = null; e.target.src = '/assets/brand/default_img.png'; }}
                        />
                    </div>
                );
            }
        },
        {
            field: 'name',
            headerName: t('toolPassportName'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'left',
            align: 'left',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            )
        },
        {
            field: 'typeOfToolName',
            headerName: t('typeOfTool'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            )
        },
        {
            field: 'subcategoryName',
            headerName: t('subcategory'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            )
        },
        {
            field: 'categoryName',
            headerName: t('category'),
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            )
        },
        {
            field: 'panelName',
            headerName: t('panel'),
            width: 120,
            headerAlign: 'center',
            align: 'center',
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            )
        },
        {
            field: 'details',
            headerName: t('details'),
            width: 120,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderHeader: (params) => (
                <span style={{ fontWeight: 600, color: '#ffffff' }}>{params.colDef.headerName}</span>
            ),
            renderCell: (params) => (
                <button
                    onClick={() => handleOpenModal(params.row)}
                    className="btn btn-sm"
                    style={{
                        backgroundColor: '#112d4e',
                        color: '#fff',
                        border: 'none',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    <i className="bi bi-eye me-1"></i>
                    {t('view')}
                </button>
            )
        }
    ], [t]);

    // Custom styles for react-select
    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '38px',
            borderColor: '#ced4da',
            '&:hover': { borderColor: '#86b7fe' }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d'
        })
    };

    return (
        <PublicPageLayout>
            <div className="container px-3">
                {/* Header */}
                <div className="mb-4">
                    <h2 style={{ color: '#112d4e', fontWeight: '600' }}>
                        <i className="bi bi-tools me-2"></i>
                        {t('toolsCatalog')}
                    </h2>
                    <p className="text-muted">{t('toolsCatalogDescription')}</p>
                </div>

                {error && (
                    <Alert severity="error" className="mb-3">{error}</Alert>
                )}

                {/* Filters */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            {/* Search */}
                            <div className="col-md-3">
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={t('searchTools')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </div>
                            {/* Panel filter */}
                            <div className="col-md-3">
                                <Select
                                    isClearable
                                    placeholder={t('selectPanel')}
                                    options={panelOptions}
                                    value={selectedPanel}
                                    onChange={handlePanelChange}
                                    styles={selectStyles}
                                />
                            </div>
                            {/* Category filter */}
                            <div className="col-md-3">
                                <Select
                                    isClearable
                                    placeholder={t('selectCategory')}
                                    options={categoryOptions}
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    isDisabled={!selectedPanel}
                                    styles={selectStyles}
                                />
                            </div>
                            {/* Subcategory filter */}
                            <div className="col-md-3">
                                <Select
                                    isClearable
                                    placeholder={t('selectSubcategory')}
                                    options={subcategoryOptions}
                                    value={selectedSubcategory}
                                    onChange={handleSubcategoryChange}
                                    isDisabled={!selectedCategory}
                                    styles={selectStyles}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Grid */}
                <style>{`
                    .public-tools-grid .MuiDataGrid-container--top,
                    .public-tools-grid .MuiDataGrid-container--top *,
                    .public-tools-grid .MuiDataGrid-columnHeaders,
                    .public-tools-grid .MuiDataGrid-columnHeaders *,
                    .public-tools-grid [class*="MuiDataGrid-columnHeader"]:not(.MuiDataGrid-columnHeaderCheckbox) {
                        background-color: #112d4e !important;
                        background: #112d4e !important;
                    }
                    .public-tools-grid .MuiDataGrid-container--top [role="row"] *,
                    .public-tools-grid .MuiDataGrid-columnHeaderTitle,
                    .public-tools-grid .MuiDataGrid-columnHeaderTitleContainer,
                    .public-tools-grid .MuiDataGrid-columnHeaderTitleContainerContent,
                    .public-tools-grid [class*="MuiDataGrid-columnHeader"] span,
                    .public-tools-grid .MuiDataGrid-sortIcon,
                    .public-tools-grid .MuiDataGrid-menuIconButton {
                        color: #ffffff !important;
                    }
                    .public-tools-grid .MuiDataGrid-iconSeparator {
                        color: rgba(255,255,255,0.3) !important;
                    }
                    .public-tools-grid .MuiDataGrid-filler,
                    .public-tools-grid .MuiDataGrid-scrollbarFiller,
                    .public-tools-grid [class*="MuiDataGrid-filler"] {
                        background-color: #112d4e !important;
                        background: #112d4e !important;
                    }
                `}</style>
                <div className="card shadow-sm public-tools-grid">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <CircularProgress />
                            </div>
                        ) : (
                            <DataGrid
                                rows={filteredData}
                                columns={columns}
                                pageSize={pageSize}
                                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                pagination
                                autoHeight
                                disableSelectionOnClick
                                getRowId={(row) => row.id}
                                rowHeight={60}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-container--top [role=row]': {
                                        backgroundColor: '#112d4e !important'
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#112d4e !important',
                                        borderBottom: '2px solid #dee2e6'
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: '#112d4e !important',
                                        color: '#ffffff !important'
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 600,
                                        color: '#ffffff !important'
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f1f3f4'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f8f9fa'
                                    }
                                }}
                                localeText={{
                                    noRowsLabel: t('noToolsFound'),
                                    MuiTablePagination: {
                                        labelRowsPerPage: t('rowsPerPage')
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Results count */}
                {!loading && (
                    <div className="mt-3 text-muted">
                        {t('showingResults', { count: filteredData.length, total: tools.length })}
                    </div>
                )}
            </div>

            {/* Hovered logo zoom */}
            {hoveredLogo && (
                <div
                    style={{
                        position: 'fixed',
                        top: `${cursorPosition.y + 20}px`,
                        left: `${cursorPosition.x + 20}px`,
                        zIndex: 1000,
                        width: '150px',
                        height: '150px',
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                        padding: '5px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '8px'
                    }}
                >
                    <img
                        src={hoveredLogo}
                        alt="Zoomed logo"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                </div>
            )}

            {/* Tool Passport Modal */}
            <PublicToolPassportModal
                open={openModal}
                onClose={handleCloseModal}
                toolPassport={selectedRow}
            />
        </PublicPageLayout>
    );
};

export default PublicToolsList;
